import React, { Component } from "react";
import PropTypes from "prop-types";
import {TableGrid} from "@terraincognita/ui-core";
import _groupBy from "lodash/groupBy";
import _sortBy from "lodash/sortBy";
import { formatTimestamp } from "utils/helpers/timestampHelper";
import { formatDistanceToNow } from "date-fns";
import { getDisplayStatus, getStatusLabelForHistory } from "./publishStatusHelper";

export class PublishHistory extends Component {
  constructor(props) {
    super(props);
    this.handleViewDetails = this.handleViewDetails.bind(this);
  }

  handleViewDetails(publishId) {
    // Open the publish status dialog for this publishId
    if (this.props.handleViewDetailsClick) {
      this.props.handleViewDetailsClick(publishId);
    }
  }

  /**
   * Strip HTML tags from a string. Regex-based so it works during SSR
   * (the DOM-based variant in PublishManager would crash server-side).
   * Used to clean project titles that the RTE stores wrapped in <p>
   * (and possibly <b>) tags.
   * @param {String} str
   * @returns {String}
   */
  stripHtmlTags(str) {
    if (!str) return "";
    return str.replace(/<[^>]*>/g, "").trim();
  }

  /**
   * Group history entries by publishId and get the most recent entry for each publish
   */
  groupHistoryByPublishId(history) {
    if (!history || !Array.isArray(history) || history.length === 0) {
      return [];
    }

    // Group by publishId
    const grouped = _groupBy(history, "publishId");

    // Convert to array and get the first entry (most recent timestamp) for each publish
    const publishGroups = Object.keys(grouped).map(publishId => {
      const entries = _sortBy(grouped[publishId], "timestamp");
      const firstEntry = entries[0];
      const lastEntry = entries[entries.length - 1];

      /*
      Regular publishes record their bundleId on the
      "bundle-record-created" entry; reverts skip the build phase and
      stamp it on the "revert-initiated" entry instead. Check both so
      every row -- publish or revert -- carries the bundleId it
      represents. Needed by the Restore action (to identify what to
      restore) and by the back-to-back-restore guard (which compares
      bundleIds across rows).
      */
      const bundleRecordEntry = entries.find(
        e => e.status === "bundle-record-created" || e.status === "revert-initiated"
      );
      const bundleId = bundleRecordEntry?.metadata?.bundleId || null;

      // Revert events are tagged on the first history entry by the
      // API's prepRevert middleware (metadata.revert === true).
      const isRevert = firstEntry?.metadata?.revert === true;

      /*
      The API stores project titles wrapped in <p> tags (RTE artifact
      from mod-api/src/middleware/cms/index.js: project.title.en =
      `<p>${data.title.en}</p>`). prepRevert and prepKioskPublishStatus-
      PreparingData copy that value verbatim into metadata.projectName,
      so the wrapping tags arrive here intact. Strip them so the cell
      renders as plain text -- otherwise the <p> creates a block-level
      break that pushes any sibling content (like the revert icon)
      onto a new line within the table cell.
      */
      const projectName =
        this.stripHtmlTags(firstEntry?.metadata?.projectName) ||
        "Unknown Project";

      return {
        publishId,
        timestamp: firstEntry?.timestamp,
        status: lastEntry?.status,
        userID: firstEntry?.userID,
        firstname: firstEntry?.metadata?.firstname,
        lastname: firstEntry?.metadata?.lastname,
        metadata: lastEntry?.metadata || {},
        projectName,
        bundleId,
        isRevert
      };
    });

    // Sort by timestamp, most recent first
    return _sortBy(publishGroups, "timestamp").reverse();
  }

  /**
   * Get status label for display
   * Uses centralized helper to get the step label based on the status
   * @param {String} status - The status from the publish history
   * @param {Object} metadata - The metadata object from the publish history entry
   * @returns {String} The status label, with "(cancelled)" appended if cancelledAt exists
   */
  getStatusLabel(status, metadata) {
    if (!status) return "";
    
    // Get the step label for the display status
    const displayStatus = getDisplayStatus(status);
    if (!displayStatus) return "";
    
    let label = getStatusLabelForHistory(status);
    
    if (metadata && metadata.error) {
      label = "Error: " + label;
    } else if (metadata && metadata.cancelledAt) {
      label += " (cancelled)";
    }
    
    return label;
  }

  /**
   * Returns the project name with a "restore" icon appended when the
   * publish event is a revert. The icon is rendered as inline SVG so
   * it can be injected via TableGrid's dangerouslySetInnerHTML cell
   * rendering (matching how getStatusLabelWithIcon emits its icons).
   * @param {String} projectName
   * @param {Boolean} isRevert
   * @returns {String}
   */
  getProjectNameWithRevertIcon(projectName, isRevert) {
    if (!isRevert) return projectName;

    const iconSize = 16;
    // display: inline-block guards against SVG-as-block defaults, and
    // the non-breaking space between the text and the icon keeps them
    // bonded so the icon never wraps onto a line by itself.
    const iconStyle = "vertical-align: middle; margin-top: -2px; display: inline-block;";
    const viewBox = "0 -960 960 960";

    // Material Symbols "restore" icon. The <title> element provides
    // a hover tooltip for accessibility since the icon is the only
    // visual indicator that this row was created by a revert.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" height="${iconSize}" width="${iconSize}" viewBox="${viewBox}" fill="#666" style="${iconStyle}"><title>Restored from a previous publish</title><path d="M480-120q-138 0-240.5-91.5T122-440h82q14 104 92.5 172T480-200q117 0 198.5-81.5T760-480q0-117-81.5-198.5T480-760q-69 0-129 32t-101 88h110v80H120v-240h80v94q51-64 124.5-99T480-840q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-480q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-120Zm112-192L440-464v-216h80v184l128 128-56 56Z"/></svg>`;
    return projectName + "&nbsp;" + svg;
  }

  getStatusLabelWithIcon(status, metadata) {
    const label = this.getStatusLabel(status, metadata);
    if (!label) return "";

    const iconSize = 16;
    const iconStyle = "vertical-align: middle; margin-right: 4px; margin-top: -2px;";
    const viewBox = "0 -960 960 960";

    // We can't use the icons from the icons folder because they
    // are not SVGs, and we need to use SVGs because the TableGrid
    // component doesn't support icons from the icons folder.
    if (metadata && metadata.error) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" height="${iconSize}" width="${iconSize}" viewBox="${viewBox}" fill="#c62828" style="${iconStyle}"><path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>`;
      return svg + label;
    }

    if (status === "complete") {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" height="${iconSize}" width="${iconSize}" viewBox="${viewBox}" fill="#2e7d32" style="${iconStyle}"><path d="m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>`;
      return svg + label;
    }

    return label;
  }

  /**
   * Get published by name from metadata
   */
  getPublishedByName(metadata) {
    if (metadata.firstname && metadata.lastname) {
      return `${metadata.firstname} ${metadata.lastname}`;
    }
    if (metadata.firstname) {
      return metadata.firstname;
    }
    if (metadata.lastname) {
      return metadata.lastname;
    }
    return "Unknown";
  }

  render() {
    const { kioskData } = this.props;

    if (!kioskData) {
      return null;
    }

    const publishHistory = kioskData.publishHistory || [];
    const publishGroups = this.groupHistoryByPublishId(publishHistory);

    if (publishGroups.length === 0) {
      return null;
    }

    const mostRecentCompletePublishId = publishGroups.find(g => g.status === "complete")?.publishId;

    /*
    Back-to-back-restore guard: if the most recent publish event in
    history is itself a revert, the kiosk is already running that
    bundle, so restoring it again would be a no-op. Capture that
    bundleId here and suppress the Restore action below for any older
    row that points at the same bundle. publishGroups is sorted most-
    recent-first, so [0] is the latest event; only act when that
    event is flagged as a revert (metadata.revert === true on the
    first history entry, surfaced as group.isRevert).
    */
    const mostRecentGroup = publishGroups[0];
    const restoredBundleId =
      mostRecentGroup?.isRevert ? mostRecentGroup.bundleId : null;

    // Convert to TableGrid format
    const items = {};
    publishGroups.forEach((group, index) => {
      const dateStr = formatTimestamp(group.timestamp) + " (" + formatDistanceToNow(new Date(group.timestamp), { addSuffix: true }) + ")";
      const statusLabel = this.getStatusLabel(group.status, group.metadata);
      const projectName = group.projectName || "Unknown Project";
      const publishedBy = (group.firstname && group.lastname)
        ? `${group.firstname} ${group.lastname}`
        : (group.firstname || group.lastname || "Unknown");

      items[index + 1] = {
        id: index + 1,
        columns: {
          1: {
            id: 1,
            label: dateStr
          },
          2: {
            id: 2,
            label: this.getProjectNameWithRevertIcon(projectName, group.isRevert),
            sortValue: projectName
          },
          3: {
            id: 3,
            label: publishedBy
          },
          4: {
            id: 4,
            label: this.getStatusLabelWithIcon(group.status, group.metadata),
            sortValue: statusLabel || ""
          }
        },
        publishId: group.publishId,
        bundleId: group.bundleId,
        status: group.status
      };
    });

    const header = {
      1: {
        id: 1,
        label: "Date"
      },
      2: {
        id: 2,
        label: "Project"
      },
      3: {
        id: 3,
        label: "Published By"
      },
      4: {
        id: 4,
        label: "Status"
      },
      5: {
        id: 5,
        label: ""
      }
    };

    const headerStyle = {
      backgroundColor: "#f2f2f2",
      fontWeight: 600
    };

    return (
      <div style={{ padding: "0 75px 65px 75px" }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Publishing History</h3>
        <TableGrid
          items={items}
          header={header}
          headerStyle={headerStyle}
          idNode="id"
          rowActions={(item) => {
            const actions = [{ value: "details", label: "Publish details" }];
            const isCurrentVersion = item.publishId === mostRecentCompletePublishId;
            /*
            Suppress the Restore action while a publish is in flight
            on this kiosk. The API does not support concurrent
            publish/revert operations against the same kiosk, and
            allowing the user to start a revert during the publish
            would corrupt publishHistory and leave kioskData in an
            ambiguous state. Once the publish completes (or fails),
            the parent flips this prop and Restore re-appears.
            */
            /*
            Also suppress Restore when this row's bundleId matches the
            bundle the kiosk was most recently restored to (see
            restoredBundleId above). Without this guard the user could
            kick off a back-to-back restore of the same bundle, which
            would just re-do the work the kiosk already did.
            */
            const isAlreadyRestored =
              restoredBundleId && item.bundleId === restoredBundleId;
            const canRestore =
              item.bundleId &&
              item.status === "complete" &&
              !isCurrentVersion &&
              !this.props.isPublishingInProgress &&
              !isAlreadyRestored;
            if (canRestore) {
              actions.push({ value: "restore", label: "Restore this version" });
            }
            return actions;
          }}
          onRowAction={(value, item) => {
            if (value === "details" && item.publishId) {
              this.handleViewDetails(item.publishId);
            } else if (value === "restore" && item.bundleId && this.props.onRevertClick) {
              if (this.props.isPublishingInProgress) {
                /*
                Defense in depth -- the action is suppressed in
                rowActions above, but this guards against future
                changes that might re-introduce it (e.g. greying out
                instead of hiding) sending us through here anyway.
                */
                return;
              }
              this.props.onRevertClick(item.bundleId);
            }
          }}
          sortable
          multiSelectable={false}
        />
      </div>
    );
  }
}

PublishHistory.propTypes = {
  kioskData: PropTypes.object,
  projects: PropTypes.object,
  handleViewDetailsClick: PropTypes.func,
  onRevertClick: PropTypes.func,
  /*
  When true, the Restore action is suppressed on every row. Set by
  the parent (KioskManager) based on PublishManager's reported
  in-flight state to prevent concurrent publish/revert operations
  against the same kiosk.
  */
  isPublishingInProgress: PropTypes.bool
};

PublishHistory.defaultProps = {
  isPublishingInProgress: false
};
